import { createAsyncThunk } from '@reduxjs/toolkit';
import { resetCachedCandidates } from '@/store/circleCandidates';
import { fetchPendingImport } from '@/store/bootstrap/actions';

const createNewContact = async (payload, { extra: { http } }) => {
  try {
    const {
      firstName,
      lastName,
      email,
      linkedin,
      company,
      jobTitle: position,
      location,
    } = payload;
    const params = {
      first_name: firstName,
      last_name: lastName,
      email,
      linkedin_url: linkedin?.trim(),
      location,
      position,
    };
    if (company) {
      const companyField =
        typeof company === 'number' ? 'company_id' : 'company_name';
      params[companyField] = company;
    }
    let paramsToUpdate = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v)
    );
    const contacts = await http.post('/contacts', paramsToUpdate);
    return contacts;
  } catch (e) {}
};

const importLinkedinData = async ({ file }, { extra: { http } }) => {
  try {
    var formdata = new FormData();
    formdata.append('import_type', 'linkedin_csv');
    formdata.append('file', file, 'Connections.csv');
    const result = await http.post('/users/import', formdata);
    return result;
  } catch (e) {}
};

const importCsv = async (
  { file, mappings, outreachId, projectId, jobId, jobStageId, tags },
  { extra: { http }, dispatch }
) => {
  try {
    var formdata = new FormData();
    if (Array.isArray(file)) {
      file.forEach((f) => {
        formdata.append('file[]', f, f.name);
      });
    } else {
      formdata.append('file', file, file.name);
    }
    formdata.append('import_type', 'csv_import');
    formdata.append('mappings', JSON.stringify(mappings));
    if (outreachId) {
      formdata.append('outreach_id', outreachId);
    }
    // todo: need to update this, once project api is done
    if (projectId) {
      formdata.append('project_id', projectId);
    }
    if (jobId) {
      formdata.append('ats_job_id', jobId);
    }
    if (jobStageId) {
      formdata.append('ats_job_stage_id', jobStageId);
    }
    if (tags) {
      formdata.append('tags', tags);
    }
    const result = await http.post('/users/import', formdata);
    await dispatch(fetchPendingImport());
    return result;
  } catch (e) {}
};

const onUpdateContact = async (
  { payload, candidateId },
  { extra: { http }, dispatch }
) => {
  const {
    firstName,
    lastName,
    email,
    linkedin,
    company,
    jobTitle: position,
    location,
  } = payload;
  const params = {
    first_name: firstName,
    last_name: lastName,
    email,
    linkedin_url: linkedin,
    location,
    position,
  };
  if (company) {
    const companyField =
      typeof company === 'number' ? 'company_id' : 'company_name';
    params[companyField] = company;
  }

  let paramsToUpdate = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v)
  );

  const contacts = await http.put(`/contacts/${candidateId}`, paramsToUpdate);

  // Note - This reset the data in store
  dispatch(
    resetCachedCandidates({ candidateId, contact: contacts.data.contact })
  );
  return contacts;
};

const getCandidateDetails = async ({ id }, { extra: { http } }) => {
  try {
    const circleOptions = await http.get(`/contacts/${id}`);
    return circleOptions;
  } catch {}
};

const getSimilarContacts = async ({ contactId }, { extra: { http } }) => {
  try {
    const contacts = await http.post('/search/circles/similar_colleagues', {
      id: contactId,
      limit: 50,
    });
    return contacts;
  } catch (e) {}
};

const addTags = async ({ contactId, tags = [] }, { extra: { http } }) => {
  try {
    const result = await http.post(`/contacts/${contactId}/tags_add`, {
      tags: tags.join(','),
    });
    return { contactId, data: result.data };
  } catch {}
};

const removeTag = async ({ contactId, tags = [] }, { extra: { http } }) => {
  try {
    const result = await http.post(`/contacts/${contactId}/tags_remove`, {
      tags: tags.join(','),
    });
    return { contactId, data: result?.data };
  } catch {}
};

const getActivities = async ({ contactId, page = 0 }, { extra: { http } }) => {
  try {
    const activities = await http.get(`/contacts/${contactId}/activities`, {
      params: {
        page: page + 1,
        items: 50,
      },
    });
    return activities;
  } catch {}
};

const getNotes = async ({ contactId }, { extra: { http } }) => {
  try {
    const notes = await http.get(`/contacts/${contactId}/notes`, {
      params: {
        items: 50,
      },
    });
    return notes;
  } catch {}
};

const getOutreaches = async ({ contactId }, { extra: { http } }) => {
  try {
    const outreaches = await http.get(`/contacts/${contactId}/outreaches`);
    return outreaches;
  } catch {}
};

const getConversations = async ({ contactId }, { extra: { http } }) => {
  try {
    const outreaches = await http.get(`/contacts/${contactId}/messages`, {
      params: {
        items: 200,
      },
    });
    return outreaches;
  } catch {}
};

const getScheduledConversations = async (
  { contactId },
  { extra: { http } }
) => {
  try {
    const conversations = await http.get(
      `/contacts/${contactId}/scheduled_messages`,
      {
        params: {
          items: 200,
        },
      }
    );
    return conversations;
  } catch {}
};

export const fetchSimilarContacts = createAsyncThunk(
  'candidates/similar',
  getSimilarContacts
);

export const fetchCandidateById = createAsyncThunk(
  'candidates/show',
  getCandidateDetails
);

export const createContact = createAsyncThunk(
  'contact/create',
  createNewContact
);

export const updateContact = createAsyncThunk(
  'contact/update',
  onUpdateContact
);

export const importLinkedinContact = createAsyncThunk(
  'contact/linkedin/import',
  importLinkedinData
);

export const importCsvContact = createAsyncThunk(
  'contact/csv/import',
  importCsv
);

export const addContactsTags = createAsyncThunk('contacts/tags/add', addTags);
export const removeContactsTags = createAsyncThunk(
  'contacts/tags/remove',
  removeTag
);

export const fetchContactsActivities = createAsyncThunk(
  'contacts/activities/fetch',
  getActivities
);

export const fetchContactsNotes = createAsyncThunk(
  'contacts/notes/fetch',
  getNotes
);

export const fetchContactsOutreaches = createAsyncThunk(
  'contacts/outreach',
  getOutreaches
);

export const fetchContactsConversations = createAsyncThunk(
  'contacts/conversations',
  getConversations
);

export const fetchScheduledConversations = createAsyncThunk(
  'contacts/scheduled/conversations',
  getScheduledConversations
);
